/**
 * Unified Configuration Loader for Frontend
 * Single source of truth for all application settings.
 *
 * Values come from .env (REACT_APP_* variables, baked in at build time by
 * react-scripts) instead of a runtime-fetched public/frontend-config.json.
 */

class UnifiedConfig {
  static config = {
    environment: process.env.REACT_APP_ENVIRONMENT,
    server: {
      frontend_url: process.env.REACT_APP_FRONTEND_URL,
      backend_url: process.env.REACT_APP_BACKEND_URL,
      api_base_url: process.env.REACT_APP_API_BASE_URL,
      // Node backend (crm-backend-node), used only by modules already
      // migrated off PHP. See src/api/*.js.
      node_api_base_url: process.env.REACT_APP_NODE_API_BASE_URL
    }
  };

  static async load() {
    return this.config;
  }

  static async get(key, defaultValue = null) {
    const config = await this.load();
    const keys = key.split('.');
    let value = config;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return defaultValue;
      }
    }

    return value;
  }

  // Convenience methods
  static async getEnvironment() {
    return await this.get('environment', 'development');
  }

  static async getFrontendUrl() {
    return await this.get('server.frontend_url', 'http://localhost:3000');
  }

  static async getBackendUrl() {
    return await this.get('server.backend_url', 'http://localhost/crmrt_live2/backend');
  }

  static async getApiBaseUrl() {
    return await this.get('server.api_base_url', 'http://localhost/crmrt_live2/backend/api');
  }

  static async getNodeApiBaseUrl() {
    return await this.get('server.node_api_base_url', 'http://localhost:4000/api');
  }

  // Origin only (no /api suffix) - used for building absolute URLs to
  // static files the Node backend serves directly, e.g. /uploads/*.
  static async getNodeBackendUrl() {
    const apiBase = await this.getNodeApiBaseUrl();
    return apiBase.replace(/\/api\/?$/, '');
  }

  static async getAssetBaseUrl() {
    return await this.get('server.frontend_url', 'http://localhost/crmrt_live2');
  }

  static async isProduction() {
    return (await this.getEnvironment()) === 'production';
  }
}

export default UnifiedConfig;
